import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useRegenerateRecoveryCodes } from "@/lib/auth-me";
import { useToast } from "@/components/v2/Toast";
import { RecoveryCodesGrid } from "./TwoFactorSetupDialog";

/**
 * "Regenerate recovery codes" — invalidates all previous codes and
 * issues 10 fresh ones. Requires password re-entry.
 */
export function RecoveryCodesDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const regen = useRegenerateRecoveryCodes();
  const toast = useToast();

  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [codes, setCodes] = useState<string[]>([]);

  const reset = () => {
    setPassword("");
    setError(null);
    setCodes([]);
  };

  const onSubmit = async () => {
    setError(null);
    try {
      const res = await regen.mutateAsync(password);
      setCodes(res.recoveryCodes);
    } catch (e) {
      setError(humanError((e as Error).message));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Regenerate recovery codes</DialogTitle>
          <DialogDescription>
            Your old recovery codes will stop working. Save the new ones
            somewhere safe — you'll only see them once.
          </DialogDescription>
        </DialogHeader>

        {codes.length === 0 && (
          <>
            <div className="grid gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="regen-password">Password</Label>
                <Input
                  id="regen-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              {error && (
                <p className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2">
                  {error}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={onSubmit} disabled={!password || regen.isPending}>
                {regen.isPending ? "Generating…" : "Regenerate codes"}
              </Button>
            </DialogFooter>
          </>
        )}

        {codes.length > 0 && (
          <>
            <RecoveryCodesGrid codes={codes} />
            <DialogFooter>
              <Button
                onClick={() => {
                  toast.success("Codes regenerated");
                  reset();
                  onClose();
                }}
              >
                Done
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function humanError(code: string): string {
  switch (code) {
    case "invalid_credentials":
      return "Password is incorrect.";
    case "mfa_not_enabled":
      return "Two-factor authentication isn't enabled.";
    default:
      return code || "Couldn't regenerate codes.";
  }
}
